import PropTypes from "prop-types";

// Pill-shaped social login buttons (Google / Facebook / Apple) shown above the
// email/password form, followed by an "ou" separator. Each provider is enabled
// only when its handler is provided; the others render disabled with a
// "coming soon" tooltip so the row already reflects the final design.

const GoogleIcon = () => (
	<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
		<path
			fill="#EA4335"
			d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
		/>
		<path
			fill="#4285F4"
			d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
		/>
		<path
			fill="#FBBC05"
			d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
		/>
		<path
			fill="#34A853"
			d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
		/>
	</svg>
);

const FacebookIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
		<path
			fill="#1877F2"
			d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12z"
		/>
		<path
			fill="#fff"
			d="M16.671 15.469L17.203 12h-3.328V9.75c0-.949.465-1.874 1.956-1.874h1.513V4.923s-1.374-.235-2.686-.235c-2.741 0-4.533 1.661-4.533 4.668V12H7.078v3.469h3.047v8.385a12.09 12.09 0 003.75 0v-8.385h2.796z"
		/>
	</svg>
);

const AppleIcon = () => (
	<svg width="18" height="18" viewBox="0 0 384 512" aria-hidden="true">
		<path
			fill="#000"
			d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
		/>
	</svg>
);

const buttonStyle = enabled => ({
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	gap: 8,
	padding: "9px 20px",
	borderRadius: 24,
	border: "1px solid #e0e0e0",
	backgroundColor: "#fff",
	color: "#3C4858",
	fontSize: 15,
	fontWeight: 500,
	lineHeight: 1,
	cursor: enabled ? "pointer" : "not-allowed",
	opacity: enabled ? 1 : 0.45,
	transition: "box-shadow 0.2s ease, border-color 0.2s ease"
});

const SocialButton = ({ icon, label, onClick, disabled }) => {
	const enabled = Boolean(onClick) && !disabled;
	return (
		<button
			type="button"
			onClick={enabled ? onClick : undefined}
			disabled={!enabled}
			title={onClick ? undefined : "Bientôt disponible"}
			style={buttonStyle(enabled)}
		>
			{icon}
			{label}
		</button>
	);
};

SocialButton.propTypes = {
	icon: PropTypes.node.isRequired,
	label: PropTypes.string.isRequired,
	onClick: PropTypes.func,
	disabled: PropTypes.bool
};

export default function SocialLoginButtons({
	onGoogle,
	onFacebook,
	onApple,
	disabled
}) {
	return (
		<div style={{ width: "100%" }}>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					justifyContent: "center",
					gap: 12
				}}
			>
				<SocialButton
					icon={<GoogleIcon />}
					label="Google"
					onClick={onGoogle}
					disabled={disabled}
				/>
				<SocialButton
					icon={<FacebookIcon />}
					label="Facebook"
					onClick={onFacebook}
					disabled={disabled}
				/>
				<SocialButton
					icon={<AppleIcon />}
					label="Apple"
					onClick={onApple}
					disabled={disabled}
				/>
			</div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					margin: "20px 0 4px"
				}}
			>
				<span style={{ flex: 1, borderTop: "1px solid #e0e0e0" }} />
				<span
					style={{
						padding: "0 14px",
						color: "#999",
						fontSize: 13,
						fontWeight: 700,
						textTransform: "uppercase"
					}}
				>
					ou
				</span>
				<span style={{ flex: 1, borderTop: "1px solid #e0e0e0" }} />
			</div>
		</div>
	);
}

SocialLoginButtons.propTypes = {
	onGoogle: PropTypes.func,
	onFacebook: PropTypes.func,
	onApple: PropTypes.func,
	disabled: PropTypes.bool
};
